import {
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  LogLevel,
} from "@azure/msal-browser";
import type { OfficeAddinSurface } from "@workspace/services/domain";
import { getMsalClientConfig } from "./msal-clients";

const MSAL_TENANT_ID = process.env.NEXT_PUBLIC_MICROSOFT_TENANT_ID ?? "common";

/**
 * Dev mode — both MSAL log verbosity and the standalone status traces
 * below are gated on this so production console output stays quiet.
 * Tree-shaken in production builds because Next.js statically replaces
 * `process.env.NODE_ENV` at build time.
 */
const DEV = process.env.NODE_ENV !== "production";

/**
 * Build the MSAL configuration for a given Office surface. Each surface
 * has its own Entra app registration (see `msal-clients.ts`); the tenant
 * is shared across all add-in surfaces.
 *
 * Uses `createNestablePublicClientApplication` downstream so the Office
 * host acts as the authentication broker, removing the need for
 * third-party cookies.
 */
function buildMsalConfig(surface: OfficeAddinSurface): Configuration {
  const { clientId } = getMsalClientConfig(surface);
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`,
      redirectUri: typeof window === "undefined" ? "/" : window.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
    },
    system: {
      loggerOptions: {
        // Verbose floods the console; restrict to Warning+ in production so
        // real issues stay visible without drowning everything else.
        logLevel: DEV ? LogLevel.Verbose : LogLevel.Warning,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }

          // Known-benign: MSAL warns when no cached tokens exist on the first
          // ssoSilent call — the NAA bridge acquires them fresh immediately
          // after.  Downgrade to debug so it doesn't pollute the console.
          if (
            level === LogLevel.Warning &&
            message.includes("Cached tokens are not found")
          ) {
            if (DEV) {
              console.debug("[MSAL]", message);
            }
            return;
          }

          switch (level) {
            case LogLevel.Error:
              console.error("[MSAL]", message);
              break;
            case LogLevel.Warning:
              console.warn("[MSAL]", message);
              break;
            case LogLevel.Info:
              if (DEV) {
                console.info("[MSAL]", message);
              }
              break;
            case LogLevel.Verbose:
              if (DEV) {
                console.debug("[MSAL]", message);
              }
              break;
            default:
              break;
          }
        },
      },
    },
  };
}

/**
 * Default scopes for a given surface. Pulled from the registry so each
 * Entra app registration's configured permissions are the source of
 * truth.
 */
export function getDefaultScopes(surface: OfficeAddinSurface): string[] {
  // Copy the readonly array into a mutable one — MSAL's request types
  // accept `string[]`, and consumers (e.g. logout, token refresh code)
  // sometimes need to extend the list.
  return [...getMsalClientConfig(surface).scopes];
}

/**
 * Per-surface MSAL instance cache. Each Office surface uses a separate
 * Entra app registration, so it gets its own MSAL client (separate
 * token cache, separate clientId in localStorage).
 */
const msalInstances = new Map<OfficeAddinSurface, IPublicClientApplication>();

/**
 * Initialise the MSAL instance as a nestable public client (NAA) for a
 * given Office surface.
 *
 * This must be called once per surface before any token requests. The
 * nestable variant allows the Office host to broker authentication,
 * avoiding third-party cookie issues in iframes / webviews.
 */
export async function initMsal(
  surface: OfficeAddinSurface
): Promise<IPublicClientApplication> {
  const cached = msalInstances.get(surface);
  if (cached) {
    return cached;
  }

  if (DEV) {
    console.info(
      `[Magnus Add-in] Initializing MSAL for Nested App Auth (${surface})`
    );
  }
  const instance = await createNestablePublicClientApplication(
    buildMsalConfig(surface)
  );
  msalInstances.set(surface, instance);
  if (DEV) {
    console.info(
      `[Magnus Add-in] MSAL instance created successfully (${surface})`
    );
  }
  return instance;
}

/**
 * Get the MSAL instance for a surface. Throws if `initMsal` has not been
 * called for that surface.
 */
export function getMsalInstance(
  surface: OfficeAddinSurface
): IPublicClientApplication {
  const instance = msalInstances.get(surface);
  if (!instance) {
    throw new Error(
      `MSAL has not been initialized for surface "${surface}". ` +
        `Call initMsal("${surface}") first.`
    );
  }
  return instance;
}

/**
 * Attempt to acquire a token, using the best strategy for the current state.
 *
 * 1. If a cached account exists → `acquireTokenSilent` (fast, no UI).
 * 2. If no cached account → `ssoSilent` (uses the Office host's SSO session
 *    via the NAA bridge — no popup, no user interaction needed).
 * 3. Last resort → `acquireTokenPopup` (Office dialog prompt).
 *
 * `scopes` defaults to the registry-configured default for the surface
 * when omitted.
 */
export async function acquireToken(
  surface: OfficeAddinSurface,
  scopes?: string[],
  loginHint?: string
): Promise<AuthenticationResult> {
  const instance = getMsalInstance(surface);
  const requestScopes = scopes ?? getDefaultScopes(surface);

  const accounts = instance.getAllAccounts();
  const account: AccountInfo | undefined = accounts[0];

  // --- Strategy 1: cached account → acquireTokenSilent -------------------
  if (account) {
    try {
      if (DEV) {
        console.info(
          "[Magnus Add-in] Attempting silent token acquisition (cached account)"
        );
      }
      const result = await instance.acquireTokenSilent({
        scopes: requestScopes,
        account,
        ...(loginHint ? { loginHint } : {}),
      });
      if (DEV) {
        console.info("[Magnus Add-in] Token acquired silently");
      }
      return result;
    } catch (silentError) {
      // Real failure path — keep visible so production observability
      // catches misconfigured Entra apps / scope drift.
      console.warn(
        "[Magnus Add-in] Silent acquisition failed for cached account",
        silentError
      );
      // Fall through to ssoSilent / popup below
    }
  }

  // --- Strategy 2: no cached account → ssoSilent via NAA bridge ----------
  try {
    if (DEV) {
      console.info("[Magnus Add-in] Attempting ssoSilent (NAA bridge)");
    }
    const result = await instance.ssoSilent({
      scopes: requestScopes,
      ...(loginHint ? { loginHint } : {}),
    });
    if (DEV) {
      console.info("[Magnus Add-in] Token acquired via ssoSilent");
    }
    return result;
  } catch (ssoError) {
    // Falling back to popup is expected on cold start when the host can't
    // provide an SSO hint, but it changes the UX (user sees a dialog), so
    // surface it in production too.
    console.warn(
      "[Magnus Add-in] ssoSilent failed, falling back to popup",
      ssoError
    );
  }

  // --- Strategy 3: last resort → acquireTokenPopup -----------------------
  if (DEV) {
    console.info("[Magnus Add-in] Attempting acquireTokenPopup");
  }
  const result = await instance.acquireTokenPopup({
    scopes: requestScopes,
    loginHint,
  });
  if (DEV) {
    console.info("[Magnus Add-in] Token acquired via popup");
  }
  return result;
}
